import React, { useState, useEffect, useCallback, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Search, Download, ChevronDown } from "lucide-react";
import ConfirmActionModal from "../../components/ConfirmActionModal";
import MerchantDetail from "./MerchantDetail";
import { adminServices } from "../../services/AdminServices";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const MerchantManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterValue, setFilterValue] = useState("All");
  const [facility, setFacility] = useState("");
  const [facilities, setFacilities] = useState([{ value: "", label: "Facility" }]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantsData, setMerchantsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [confirmStatusModal, setConfirmStatusModal] = useState({
    open: false,
    merchantId: null,
    merchantName: "",
    nextStatusId: null
  });

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await adminServices.postData("sp/v2/getallfacilities", {});
        const data = response?.data;
        if (Array.isArray(data)) {
          const options = [{ value: "", label: "Facility" }];
          data.forEach((fac) => {
            const id = fac.facilityId;
            const name = fac.facilityName;
            if (id && name) {
              options.push({
                value: String(id),
                label: `${id} - ${name}`
              });
            }
          });
          setFacilities(options);
        } else {
          setFacilities([{ value: "", label: "Facility" }]);
        }
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([{ value: "", label: "Facility" }]);
      }
    };
    fetchFacilities();
  }, []);

  // Facility filter should reset pagination and merchant list
  useEffect(() => {
    setPage(1);
  }, [facility]);

  const mapMerchants = (raw) =>
    raw.map((m) => ({
      id: m.id ?? m.merchantId ?? "",
      merchantId: m.id ?? m.merchantId,
      merchantName: m.merchant_name ?? m.merchantName ?? m.name ?? "",
      pdlNumber: m.pdl_number ?? m.pdlNumber ?? "",
      storeCode: m.merchantCode ?? m.storeCode ?? m.code ?? "",
      facility: m.quadrant ?? m.facilityName ?? m.facility ?? "",
      location: m.location ?? "",
      status: m.status === 1 || m.status === "active" ? "active" : "inactive"
    }));

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      let raw = [];
      if (facility) {
        const res = await adminServices.postData("sp/v2/getMerchantByFacility", {
          facilityId: facility
        });
        raw = Array.isArray(res?.data) ? res.data : [];
      } else {
        const res = await adminServices.getInventoryMerchants(searchKeyword, page, PAGE_SIZE);
        raw = Array.isArray(res?.data) ? res.data : [];
      }
      setMerchantsData(mapMerchants(raw));
    } catch (err) {
      console.error("Failed to fetch merchants:", err);
      setMerchantsData([]);
    } finally {
      setLoading(false);
    }
  }, [facility, searchKeyword, page]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  // Debounce search keyword before sending to API; reset page on search change
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchKeyword(searchQuery.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Filter by status (All / Active / Inactive)
  const filteredMerchants = useMemo(() => {
    if (filterValue === "All") return merchantsData;
    const target = filterValue.toLowerCase();
    return merchantsData.filter((m) => (m.status ?? "").toLowerCase() === target);
  }, [merchantsData, filterValue]);

  const handleExportCsv = () => {
    if (filteredMerchants.length === 0) return;
    const headers = ["Merchant Name", "PDL Number", "Store Code", "Facility", "Location", "Status"];
    const rows = filteredMerchants.map((m) => [
      (m.merchantName ?? "").replace(/"/g, '""'),
      m.pdlNumber ?? "",
      m.storeCode ?? "",
      (m.facility ?? "").replace(/"/g, '""'),
      (m.location ?? "").replace(/"/g, '""'),
      m.status ?? ""
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(","))
    ].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `merchants-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      name: "Merchant Name",
      cell: row => {
        if (!row.merchantName || row.merchantName.trim() === "") return <div>-</div>;
        return (
          <div 
            style={{ cursor: "pointer", color: "#3b82f6" }}
            onClick={(e) => {
              e.stopPropagation();
              if (row && row.id && row.merchantName) {
                setSelectedMerchant(row);
              }
            }}
          >
            {row.merchantName}
          </div>
        );
      },
      sortable: true,
      width: "20%"
    },
    {
      name: "PDL number",
      selector: row => row.pdlNumber || "-",
      sortable: true,
      width: "18%"
    },
    {
      name: "Store Code",
      selector: row => row.storeCode ? `(${row.storeCode})` : "-",
      sortable: true,
      width: "12%"
    },
    {
      name: "Facility",
      selector: row => row.facility || "-",
      sortable: true,
      width: "12%"
    },
    {
      name: "Location",
      selector: row => row.location || "-",
      sortable: true,
      width: "15%"
    },
    {
      name: "Status",
      cell: row => {
        if (!row.status) return <div></div>;
        const isActive = row.status === "active";
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <span
              className={`merchant-status-badge ${isActive ? "active" : "inactive"}`}
            >
              {row.status}
            </span>
          </div>
        );
      },
      sortable: true,
      width: "12%",
      center: true
    },
    {
      name: "Actions",
      cell: row => {
        if (!row.status) return <div></div>;
        const isActive = row.status === "active";
        const merchantKey = row.merchantId ?? row.id;
        const isUpdating = Boolean(merchantKey && updatingStatus[String(merchantKey)]);
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <button 
              className="merchant-action-btn"
              disabled={isUpdating}
              onClick={(e) => {
                e.stopPropagation();
                const merchantId = row.merchantId ?? row.id;
                if (!merchantId) return;

                const nextStatusId = isActive ? 0 : 1;
                setConfirmStatusModal({
                  open: true,
                  merchantId,
                  merchantName: row.merchantName ?? "",
                  nextStatusId
                });
              }}
            >
              {isUpdating ? "Updating..." : (isActive ? "Set as Inactive" : "Set as Active")}
            </button>
          </div>
        );
      },
      sortable: false,
      width: "11%",
      center: true
    }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    }
  };

  // Show detail view if merchant is selected
  if (selectedMerchant) {
    return (
      <MerchantDetail 
        merchant={selectedMerchant} 
        onBack={() => {
          setSelectedMerchant(null);
          fetchMerchants();
        }} 
      />
    );
  }

  return (
    <div className="dashboard-content">
      <div className="merchant-management-page">
        <div className="merchant-management-header">
          <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: 20 }}>
            <h1 className="dashboard-title" style={{ margin: 0 }}>Merchant Management</h1>
            <div className="dashboard-facility-dropdown">
              <select
                className="dashboard-facility-select"
                value={facility}
                onChange={(e) => setFacility(e.target.value)}
              >
                {facilities.map((opt) => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="dashboard-facility-icon" size={18} />
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="merchant-management-search-filter">
          <div className="merchant-search-container">
            <Search className="merchant-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="merchant-search-input"
            />
          </div>
          <div className="merchant-filter-export">
            <select
              className="merchant-filter-select"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <button className="merchant-export-btn" onClick={handleExportCsv} disabled={filteredMerchants.length === 0}>
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="merchant-management-table-section">
          <div className="merchant-table-container">
            <DataTable
              columns={columns}
              data={filteredMerchants}
              progressPending={loading}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination={false}
              onRowClicked={(row) => {
                // Only allow clicking rows with valid merchant data
                if (row && row.id && row.merchantName && row.merchantName.trim() !== "") {
                  setSelectedMerchant(row);
                }
              }}
              pointerOnHover
            />
          </div>
        </div>
      </div>

      <ConfirmActionModal
        isOpen={confirmStatusModal.open}
        title={confirmStatusModal.nextStatusId === 1 ? "Set as Active" : "Set as Inactive"}
        message={
          <>
            Are you sure you want to
            <br />
            {confirmStatusModal.nextStatusId === 1 ? "set as Active" : "set as Inactive"}?
          </>
        }
        confirmLabel={confirmStatusModal.nextStatusId === 1 ? "Confirm" : "Inactive"}
        confirmColor={confirmStatusModal.nextStatusId === 1 ? "#0F2A40" : "#b91c1c"}
        confirmDisabled={!confirmStatusModal.merchantId}
        onClose={() => setConfirmStatusModal({ open: false, merchantId: null, merchantName: "", nextStatusId: null })}
        onConfirm={() => {
          const merchantId = confirmStatusModal.merchantId;
          const nextStatusId = confirmStatusModal.nextStatusId;
          if (!merchantId || (nextStatusId !== 0 && nextStatusId !== 1)) return;

          setUpdatingStatus((prev) => ({ ...prev, [String(merchantId)]: true }));
          setConfirmStatusModal({ open: false, merchantId: null, merchantName: "", nextStatusId: null });

          adminServices
            .setMerchantStatusV2(nextStatusId, merchantId)
            .then(() => {
              setMerchantsData((prev) =>
                prev.map((m) =>
                  String(m.merchantId ?? m.id) === String(merchantId)
                    ? { ...m, status: nextStatusId === 1 ? "active" : "inactive" }
                    : m
                )
              );
            })
            .catch((err) => {
              console.error("Failed to update merchant status:", err);
              fetchMerchants();
              window.alert("Failed to update merchant status. Please try again.");
            })
            .finally(() => {
              setUpdatingStatus((prev) => {
                const next = { ...prev };
                delete next[String(merchantId)];
                return next;
              });
            });
        }}
      />
    </div>
  );
};

export default MerchantManagement;

